import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore, isAdmin, ROLES } from '../shared/store/authStore'

// ── Shared ────────────────────────────────────────────────
import LoginPage    from '../LoginPage'
import TabletShell  from '../shared/components/TabletShell'

// ── Dashboards (role-specific) ───────────────────────────
import DashboardPage from '../roles/metallic-master/pages/DashboardPage'
import LSDashboardPage from '../roles/loading-supervisor/pages/DashboardPage'

// ── Metallic Master pages ─────────────────────────────────
import CuttingPage   from '../roles/metallic-master/pages/CuttingPage'
import BobbinPage    from '../roles/metallic-master/pages/BobbinPage'

// ── Loading Supervisor pages ──────────────────────────────
import SpindlePage   from '../roles/loading-supervisor/pages/SpindlePage'
import OutputPage    from '../roles/loading-supervisor/pages/OutputPage'
import BoilingPage   from '../roles/loading-supervisor/pages/BoilingPage'
import ConePage      from '../roles/loading-supervisor/pages/ConePage'

// ── Admin layout + pages ──────────────────────────────────
import AdminLayout          from '../roles/admin/components/AdminLayout'
import AdminDashboard       from '../roles/admin/pages/AdminDashboard'
import NotificationsPage    from '../roles/admin/pages/NotificationsPage'
import UnitsPage            from '../roles/admin/pages/UnitsPage'
import UsersPage            from '../roles/admin/pages/UsersPage'
import PartiesPage          from '../roles/admin/pages/PartiesPage'
import VendorsPage          from '../roles/admin/pages/VendorsPage'
import ProcurementPage      from '../roles/admin/pages/ProcurementPage'
import ReportsPage          from '../roles/admin/pages/ReportsPage'
import CostingPage          from '../roles/admin/pages/CostingPage'
import MachinesPage        from '../roles/admin/pages/MachinesPage'
import OperatorsPage       from '../roles/admin/pages/OperatorsPage'
import AnnouncementsPage   from '../roles/admin/pages/AnnouncementsPage'
import DispatchPage        from '../roles/admin/pages/DispatchPage'
import ShadeMastersPage    from '../roles/admin/pages/ShadeMastersPage'
import ContainersPage      from '../roles/admin/pages/ContainersPage'
import CuttingRecordsPage   from '../roles/admin/pages/CuttingRecordsPage'
import InitializedRollsPage from '../roles/admin/pages/InitializedRollsPage'
import BobbinBatchesPage    from '../roles/admin/pages/BobbinBatchesPage'
import DispatchListPage    from '../roles/loading-supervisor/pages/DispatchListPage'

function RequireAuth({ children, allowedRoles }) {
  const { isAuthenticated, user, logout } = useAuthStore()
  
  if (!isAuthenticated) return <Navigate to="/login" replace />

  // If user data is missing but authenticated, force logout to clear broken state
  if (!user || !user.role) {
    logout()
    return <Navigate to="/login" replace />
  }

  const isAuthorized = allowedRoles && allowedRoles.includes(user.role)

  if (!isAuthorized) {
    // If not authorized for this specific area, try to find their home
    if (isAdmin(user.role)) return <Navigate to="/admin" replace />
    
    const isSup = [ROLES.METALLIC_MASTER, ROLES.LOADING_SUPERVISOR].includes(user.role)
    if (isSup) return <Navigate to="/app/dashboard" replace />

    // If they aren't admin OR supervisor, show unauthorized instead of looping
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h1>
        <p className="text-gray-500 max-w-xs mb-6">You don't have permission to access this section of the application.</p>
        <button 
          onClick={() => {
            logout()
            window.location.href = '/login'
          }} 
          className="bg-brand-600 text-white px-6 py-2 rounded-xl font-medium shadow-sm hover:translate-y-[-1px] transition-all"
        >
          Logout & Back to Login
        </button>
      </div>
    )
  }

  return children
}

function AdminShell({ children }) {
  return (
    <RequireAuth allowedRoles={[ROLES.ADMIN, ROLES.COMPANY_HEAD]}>
      <AdminLayout>{children}</AdminLayout>
    </RequireAuth>
  )
}

function SupervisorShell() {
  return (
    <RequireAuth allowedRoles={[ROLES.METALLIC_MASTER, ROLES.LOADING_SUPERVISOR]}>
      <TabletShell />
    </RequireAuth>
  )
}

// ── Role-specific dashboard ──────────────────────────────
function RoleDashboard() {
  const { user } = useAuthStore()
  return user?.role === 'loading_supervisor'
    ? <LSDashboardPage />
    : <DashboardPage />
}

// ── App Router ────────────────────────────────────────────
export default function AppRouter() {
  const { isAuthenticated, user } = useAuthStore()
  const defaultPath = isAuthenticated
    ? isAdmin(user?.role) ? '/admin' : '/app/dashboard'
    : '/login'

  return (
    <Routes>
      {/* Public */}
      <Route
        path="/login"
        element={
          isAuthenticated
            ? <Navigate to={defaultPath} replace />
            : <LoginPage />
        }
      />

      {/* ── Supervisor tablet app ── */}
      <Route path="/app" element={<SupervisorShell />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={
          <RoleDashboard />
        } />

        {/* Metallic Master */}
        <Route path="cutting/*" element={<CuttingPage />} />
        <Route path="bobbin/*"  element={<BobbinPage />} />

        {/* Loading Supervisor */}
        <Route path="spindle/*" element={<SpindlePage />} />
        <Route path="output/*"  element={<OutputPage />} />
        <Route path="boiling/*" element={<BoilingPage />} />
        <Route path="cone/*"      element={<ConePage />} />
        <Route path="dispatch"    element={<DispatchListPage />} />
      </Route>

      {/* ── Admin web panel ── */}
      <Route path="/admin"                element={<AdminShell><AdminDashboard /></AdminShell>} />
      <Route path="/admin/notifications"  element={<AdminShell><NotificationsPage /></AdminShell>} />
      <Route path="/admin/units"          element={<AdminShell><UnitsPage /></AdminShell>} />
      <Route path="/admin/machines"       element={<AdminShell><MachinesPage /></AdminShell>} />
      <Route path="/admin/operators"      element={<AdminShell><OperatorsPage /></AdminShell>} />
      <Route path="/admin/users"          element={<AdminShell><UsersPage /></AdminShell>} />
      <Route path="/admin/parties"        element={<AdminShell><PartiesPage /></AdminShell>} />
      <Route path="/admin/vendors"        element={<AdminShell><VendorsPage /></AdminShell>} />
      <Route path="/admin/shades"         element={<AdminShell><ShadeMastersPage /></AdminShell>} />
      <Route path="/admin/containers"     element={<AdminShell><ContainersPage /></AdminShell>} />
      <Route path="/admin/procurement"    element={<AdminShell><ProcurementPage /></AdminShell>} />
      <Route path="/admin/cutting-logs"   element={<AdminShell><CuttingRecordsPage /></AdminShell>} />
      <Route path="/admin/initialized-rolls" element={<AdminShell><InitializedRollsPage /></AdminShell>} />
      <Route path="/admin/bobbin-batches"  element={<AdminShell><BobbinBatchesPage /></AdminShell>} />
      <Route path="/admin/dispatch"       element={<AdminShell><DispatchPage /></AdminShell>} />
      <Route path="/admin/reports"        element={<AdminShell><ReportsPage /></AdminShell>} />
      <Route path="/admin/costing"        element={<AdminShell><CostingPage /></AdminShell>} />
      <Route path="/admin/announcements"  element={<AdminShell><AnnouncementsPage /></AdminShell>} />

      {/* Root redirect */}
      <Route path="/" element={<Navigate to={defaultPath} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
